import { AnalyticsUserEvent } from '@fruitful/types';
import { useIsFocused } from '@react-navigation/native';
import { router } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    H5Emphasized,
    SafeAreaView,
    Spinner,
    tokens,
    View,
    XStack,
    YStack,
} from 'smoothie';

import FruitfulLogoIcon from '../../../../assets/images/logos/fruitful-logo-icon-only-white.svg';
import { spinwheelLiabilitiesAtom } from '../../../bills/state/BillsAtoms';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { track } from '../../../logger/Logger';
import { useGetCreditProfile } from '../../hooks/useGetCreditProfile';
import { useGetLiabilities } from '../../hooks/useGetLiabilities';

const SpinwheelAccountVerification = () => {
    const isFocused = useIsFocused();

    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`Onboarding/SpinwheelAccountVerification`);

    // Atoms for storing liabilities data
    const setSpinwheelLiabilities = useSetAtom(spinwheelLiabilitiesAtom);
    const spinwheelLiabilities = useAtom(spinwheelLiabilitiesAtom)[0];

    // Call credit profile API
    const { data, isLoading, isError, error } = useGetCreditProfile({});

    // Call liabilities API (only after credit profile succeeds)
    const {
        data: liabilitiesData,
        isLoading: isLoadingLiabilities,
        isError: isLiabilitiesError,
        refetch: refetchLiabilities,
    } = useGetLiabilities();

    const goToNextScreen = () => {
        track(
            AnalyticsUserEvent.ONBOARDING_FUNNEL_SPINWHEEL_COMPLETED,
            undefined,
            true,
        );
        router.navigate('/onboarding/expenses/bills-processing');
    };

    // Store liabilities data when received
    useEffect(() => {
        if (liabilitiesData && !isLoadingLiabilities) {
            LogRocketFacade.log(
                `Liabilities data received: ${JSON.stringify(liabilitiesData)}`,
            );
            setSpinwheelLiabilities(liabilitiesData);
        }
    }, [liabilitiesData, isLoadingLiabilities, setSpinwheelLiabilities]);

    // Refetch liabilities when credit profile is complete (in case we got empty data before)
    const [hasRefetchedForEmptyData, setHasRefetchedForEmptyData] =
        useState(false);

    useEffect(() => {
        if (data && !isLoading && !hasRefetchedForEmptyData) {
            // Check if we have empty liabilities data and refetch ONCE
            const hasEmptyLiabilities =
                spinwheelLiabilities?.liabilities?.creditCards?.length === 0 &&
                spinwheelLiabilities?.liabilities?.autoLoans?.length === 0 &&
                spinwheelLiabilities?.liabilities?.homeLoans?.length === 0;

            if (hasEmptyLiabilities) {
                LogRocketFacade.log(
                    'Credit profile complete, refetching liabilities once...',
                );
                setHasRefetchedForEmptyData(true);
                void refetchLiabilities();
            }
        }
    }, [data, isLoading, hasRefetchedForEmptyData]); // Removed spinwheelLiabilities and refetchLiabilities

    useEffect(() => {
        if (isFocused && data && !isLoading && spinwheelLiabilities) {
            // Add 8 second delay before navigating to next screen
            const timer = setTimeout(() => {
                goToNextScreen();
            }, 2000);

            return () => {
                clearTimeout(timer);
            };
        }
        return undefined;
    }, [data, isLoading, spinwheelLiabilities]);

    useEffect(() => {
        if (isError) {
            LogRocketFacade.log(
                `Credit profile fetch failed: ${JSON.stringify(error)}`,
            );

            // Check for specific error code
            const errorCode = (
                error as Error & { json?: { errorCode?: string } }
            )?.json?.errorCode;
            if (errorCode === 'UNABLE_TO_FIND_CREDIT_REPORT') {
                router.navigate(
                    '/onboarding/expenses/unable-to-find-credit-report',
                );
            } else {
                // For other errors, navigate to generic error page
                router.navigate({
                    pathname: '/onboarding/expenses/error',
                    params: {
                        title: 'Unable to retrieve your credit profile',
                        description:
                            'We encountered an issue retrieving your credit information. Please try again.',
                        primaryButtonText: 'Try again',
                        primaryButtonRoute: '/onboarding/expenses/disclaimer',
                    },
                });
            }
        }
    }, [isError, error]);

    // Handle liabilities errors
    useEffect(() => {
        if (isLiabilitiesError) {
            LogRocketFacade.log(
                'Liabilities fetch failed, but continuing to bills review',
            );
            // Still navigate to bills review even if liabilities fail
            if (data && !isLoading) {
                goToNextScreen();
            }
        }
    }, [isLiabilitiesError, data, isLoading]);

    const handleGoBack = () => {
        router.back();
    };

    return (
        <SafeAreaView
            style={{
                flex: 1,
                backgroundColor: tokens.color.light.brand.white,
                maxWidth: 700,
                minWidth: 400,
                alignSelf: 'center',
            }}
        >
            <YStack flex={1}>
                {/* Header */}
                <XStack
                    paddingHorizontal={24}
                    paddingTop={16}
                    paddingBottom={16}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <BackButton
                        fallbackRoute="/home"
                        customBackFn={handleGoBack}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                    <XStack width={40} />
                </XStack>

                {/* Content */}
                <YStack flex={1} paddingHorizontal={24}>
                    <YStack alignItems="flex-start" gap={16} marginBottom={80}>
                        <H5Emphasized color="$black" textAlign="left">
                            Gathering your recurring bills
                        </H5Emphasized>
                        <BodyMediumRegular color="$gray1000" textAlign="left">
                            Please wait a few minutes.
                        </BodyMediumRegular>
                    </YStack>

                    {/* Rotating Institution Animation */}
                    <YStack gap={12} width="100%">
                        <XStack
                            key="none"
                            alignItems="center"
                            paddingVertical={16}
                            paddingHorizontal={20}
                            backgroundColor="$gray100"
                            borderRadius={12}
                            animation="lazy"
                            enterStyle={{
                                opacity: 0,
                                y: -20,
                            }}
                            exitStyle={{
                                opacity: 0,
                                y: 20,
                            }}
                        >
                            {/* Institution Logo/Icon - Fixed 48px */}
                            <View
                                width={48}
                                height={48}
                                borderRadius={12}
                                backgroundColor="$green900"
                                alignItems="center"
                                justifyContent="center"
                                marginRight={12}
                            >
                                <FruitfulLogoIcon width={32} height={32} />
                            </View>

                            {/* Institution Name - Fills remaining space */}
                            <BodyMediumRegular
                                color="$black"
                                flexShrink={1}
                                flexGrow={1}
                            >
                                Connecting Debts
                            </BodyMediumRegular>

                            {/* Status Indicator - Fixed ~24px */}
                            <View width={24} marginLeft={16}>
                                <Spinner />
                            </View>
                        </XStack>
                    </YStack>
                </YStack>
            </YStack>
        </SafeAreaView>
    );
};

export default SpinwheelAccountVerification;
